import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { Mic } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import SpeechRecognition, { useSpeechRecognition } from "react-speech-recognition";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";

interface WhisperVoiceInputButtonProps {
  onTranscript: (text: string) => void;
  onInterimTranscript?: (text: string) => void;
  onRecordingChange?: (isRecording: boolean) => void;
  /** Reports recording time in seconds; parent can show it in the bar (timestamp moved from button to bar) */
  onRecordingTimeChange?: (seconds: number) => void;
  disabled?: boolean;
  className?: string;
  size?: "default" | "sm" | "lg" | "icon";
}

export function WaveformAnimation({ className }: { className?: string }) {
  return (
    <div className={cn("flex items-center gap-0.5 h-4", className)}>
      {[0, 1, 2, 3, 4].map((i) => (
        <div
          key={i}
          className="w-0.5 bg-red-500 rounded-full animate-pulse"
          style={{
            height: `${Math.random() * 12 + 4}px`,
            animationDelay: `${i * 0.1}s`,
            animationDuration: '0.5s',
          }}
        />
      ))}
    </div>
  );
}

export type WhisperVoiceInputHandle = { stop: () => void } | null;

export const WhisperVoiceInputButton = forwardRef<WhisperVoiceInputHandle, WhisperVoiceInputButtonProps>(function WhisperVoiceInputButton({ 
  onTranscript, 
  onInterimTranscript,
  onRecordingChange,
  onRecordingTimeChange,
  disabled = false, 
  className,
  size = "icon",
}, ref) {
  const { toast } = useToast();
  const previousTranscriptRef = useRef("");
  const previousInterimTranscriptRef = useRef("");
  const recordingCountRef = useRef(0);
  const onRecordingTimeChangeRef = useRef(onRecordingTimeChange);
  onRecordingTimeChangeRef.current = onRecordingTimeChange;

  const {
    transcript,
    interimTranscript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition
  } = useSpeechRecognition();

  // Check if speech recognition is supported
  const isSupported = browserSupportsSpeechRecognition;

  // Track recording time; report to parent for bar. Defer all parent updates to next
  // tick so we never call parent setState during child render/effect (avoids
  // "Cannot update a component while rendering a different component").
  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null;
    const report = (n: number) => {
      const cb = onRecordingTimeChangeRef.current;
      if (cb) setTimeout(() => cb(n), 0);
    };
    if (listening) {
      recordingCountRef.current = 0;
      report(0);
      interval = setInterval(() => {
        recordingCountRef.current += 1;
        report(recordingCountRef.current);
      }, 1000);
    } else {
      recordingCountRef.current = 0;
      report(0);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [listening]);

  // Notify parent of recording state changes
  useEffect(() => {
    if (onRecordingChange) {
      onRecordingChange(listening);
    }
  }, [listening, onRecordingChange]);

  // Handle final transcript - send when recording stops
  useEffect(() => {
    if (!listening && transcript && transcript !== previousTranscriptRef.current) {
      // Recording stopped - send the FULL accumulated final transcript
      // Since previousTranscriptRef is reset at the start of each session,
      // transcript contains everything spoken during this recording session
      if (transcript.trim()) {
        onTranscript(transcript.trim());
      }
      // Reset everything after sending
      resetTranscript();
      previousTranscriptRef.current = "";
      if (onInterimTranscript) {
        onInterimTranscript("");
        previousInterimTranscriptRef.current = "";
      }
    } else if (listening && transcript) {
      // Don't update previousTranscriptRef during listening - we want the full transcript at the end
      // This ensures we get the complete transcript when recording stops
    }
  }, [transcript, listening, onTranscript, resetTranscript, onInterimTranscript]);

  // Handle interim transcript changes (real-time speech as user speaks)
  useEffect(() => {
    if (onInterimTranscript) {
      if (listening) {
        // Combine final transcript + current interim to form a full, cumulative view
        const combinedText = transcript
          ? (interimTranscript ? `${transcript} ${interimTranscript}` : transcript)
          : interimTranscript;

        if (combinedText) {
          // Show combined transcript in real-time
          if (combinedText !== previousInterimTranscriptRef.current) {
            previousInterimTranscriptRef.current = combinedText;
            onInterimTranscript(combinedText);
          }
        } else if (!previousInterimTranscriptRef.current || previousInterimTranscriptRef.current === "Listening...") {
          // Show "Listening..." when starting but no results yet
          onInterimTranscript("Listening...");
          previousInterimTranscriptRef.current = "Listening...";
        }
      } else {
        // Clear interim when not listening
        if (previousInterimTranscriptRef.current) {
          previousInterimTranscriptRef.current = "";
          onInterimTranscript("");
        }
      }
    }
  }, [interimTranscript, transcript, listening, onInterimTranscript]);

  const startListening = () => {
    if (!isSupported) {
      toast({
        title: "Not Supported",
        description: "Speech recognition is not supported in your browser.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Reset transcript tracking for a fresh start
      previousTranscriptRef.current = "";
      previousInterimTranscriptRef.current = "";
      resetTranscript();
      
      SpeechRecognition.startListening({
        continuous: true,
        interimResults: true,
        language: 'en-US',
      });
    } catch (error) {
      console.error("Failed to start speech recognition:", error);
      toast({
        title: "Error",
        description: "Failed to start voice input. Please check your microphone permissions.",
        variant: "destructive",
      });
    }
  };

  const stopListening = () => {
    try {
      SpeechRecognition.stopListening();
      if (onInterimTranscript) {
        onInterimTranscript("");
        previousInterimTranscriptRef.current = "";
      }
    } catch (error) {
      console.error("Failed to stop speech recognition:", error);
    }
  };

  useImperativeHandle(ref, () => ({ stop: stopListening }), []);

  if (!isSupported) {
    return null;
  }

  // When recording, UI (stop, dot, waveform, timestamp) is in the parent's bar; render nothing here.
  if (listening) {
    return null;
  }

  // Default state - microphone button
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size={size}
          onClick={startListening}
          disabled={disabled}
          className={cn("relative h-8 w-8", className)}
          data-testid="button-whisper-voice-input"
          aria-label="Start voice input"
        >
          <Mic className="h-4 w-4" />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="top">
        <p>Voice input</p>
      </TooltipContent>
    </Tooltip>
  );
});
